import type { ForwardedRef, JSX, MouseEvent, RefObject } from 'react';
import { forwardRef, useCallback, useRef } from 'react';
import mergeRefs from 'react-merge-refs';
import type { AnimationControls } from 'framer-motion';
import { AnimatePresence, motion } from 'framer-motion';
import {
  Box,
  ScrollContainer,
  Spinner,
  Stack,
  theme,
  useTimeDeferredMount,
} from '@frameio/vapor';
import { useSkeletonContext } from '@frameio/vapor-migration';
import TEXT from './generated/TEXT/InviteScrollWrapper';
import { MemberSearch } from './MemberSearch';

type InviteScrollWrapperProps = {
  adornmentAfterSearch?: JSX.Element;
  animateControls?: AnimationControls;
  children: React.ReactNode;
  deferRender?: boolean;
  fetchNextPage?: () => Promise<string[]>;
  isFirstTimeLoading?: boolean;
  loading: boolean;
  loadingIsSlow: boolean;
  placeholderCount: number;
  scrollHeight: string;
  searchInputPlaceholder?: string;
  searchTerm: string;
  setSearchTerm: (searchTerm: string) => void;
  state: any;
};

TEXT.DEFINE({
  /** Aria label for search loading spinner */
  spinnerLabel: 'Searching',
});

export const InviteScrollWrapper = forwardRef(
  (
    {
      adornmentAfterSearch,
      animateControls,
      children,
      deferRender,
      fetchNextPage,
      isFirstTimeLoading,
      loading,
      loadingIsSlow,
      placeholderCount = 0,
      scrollHeight,
      searchInputPlaceholder,
      searchTerm,
      setSearchTerm,
      state,
    }: InviteScrollWrapperProps,
    focusRefFromProps: ForwardedRef<HTMLDivElement>
  ) => {
    const focusRef = focusRefFromProps as RefObject<HTMLInputElement | null>;
    // 300ms is the length of the AccordionDialog collapse/expansion transition
    const deferredRenderIsReady = useTimeDeferredMount(310);
    const containerRef = useRef<HTMLDivElement>(
      undefined
    ) as RefObject<HTMLDivElement | null>;
    const scrollRef = useRef<HTMLDivElement>(
      undefined
    ) as RefObject<HTMLDivElement | null>;
    const { SkeletonContextProvider, viewportRef } = useSkeletonContext({
      rootMargin: `0px 0px`,
      lazy: false,
    });
    const handleScroll = useCallback(
      async (event: MouseEvent<HTMLDivElement>) => {
        const target = event.target as HTMLDivElement;
        // Fetch more if scrolled a quarter or more through the scroll container
        const scrollPosition = target?.offsetHeight + target?.scrollTop;
        const quarterScrollHeight = target?.scrollHeight / 4;
        if (scrollPosition >= quarterScrollHeight && !loading) {
          if (fetchNextPage) {
            fetchNextPage();
          }
        }
      },
      [fetchNextPage, loading]
    );
    const onScroll = (event: MouseEvent<HTMLDivElement>) => {
      event.persist();
      requestAnimationFrame(() => handleScroll(event));
    };
    const searchIsSlow = Boolean(searchTerm) && loadingIsSlow;
    return (
      <Box
        ref={containerRef}
        aria-atomic="true"
        aria-live="polite"
        aria-relevant="additions"
        style={{ minHeight: scrollHeight }}
      >
        <MemberSearch
          ref={focusRef}
          adornmentAfter={adornmentAfterSearch}
          currentValue={searchTerm}
          loading={loading}
          onChange={setSearchTerm}
          placeholder={searchInputPlaceholder}
          showNoResults={state.hasActiveSearchTerm && !state.hasResults}
        />
        <Spinner
          aria-label={TEXT.spinnerLabel}
          centered={true}
          delay={100}
          isLoading={isFirstTimeLoading || searchIsSlow}
        />

        <AnimatePresence initial={false}>
          {!isFirstTimeLoading && (!deferRender || deferredRenderIsReady) && (
            <motion.div
              animate={{ opacity: 1 }}
              initial={{ opacity: 0 }}
              style={{ height: '100%', position: 'absolute', width: '100%' }}
              transition={{ duration: 0.2 }}
            >
              {state.hasResults && (
                <SkeletonContextProvider
                  disableSkeletons={placeholderCount < 200}
                >
                  <motion.div
                    animate={animateControls}
                    style={{
                      position: 'relative',
                      height: '100%',
                      zIndex: theme.zIndices.base,
                    }}
                  >
                    <Box
                      style={{
                        height: '100%',
                        opacity: searchIsSlow ? 0 : 1,
                        transition: 'opacity 0.3s ease',
                        transitionDelay: searchIsSlow ? '0.3s' : '0s',
                      }}
                    >
                      <ScrollContainer
                        ref={mergeRefs([scrollRef, viewportRef])}
                        // 48px is accounting for the search bar height
                        height={`calc(${scrollHeight} - 48px)`}
                        onScroll={onScroll}
                        paddingX={0}
                      >
                        <Stack ref={scrollRef} gap={0}>
                          {children}
                        </Stack>
                      </ScrollContainer>
                    </Box>
                  </motion.div>
                </SkeletonContextProvider>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </Box>
    );
  }
);
